import { Link } from 'react-router';
import { Badge } from '@/components/ui/badge';
import { useCanOpen } from '@/hooks/useCanOpen';
import { formatDate, formatTimestamp } from '@/utils/dateUtils';

interface PortfolioConversationDuePayloadProps {
  payload: Record<string, unknown>;
  /** Workflow item due_at (ISO 8601), passed through from WorkflowDetailSheet. */
  dueAt?: string;
  i18n: { _: (d: { id: string; message: string }) => string };
}

/**
 * Local copy of WorkflowPayloadDetail's FieldRow. This is a detail panel, not a
 * data table, so BaseEditableTable does not apply and no raw <table> is used.
 */
function FieldRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
        {label}
      </span>
      <div className="mt-1">{children}</div>
    </div>
  );
}

/** Read a payload field as a non-blank string, or undefined. */
function readString(payload: Record<string, unknown>, key: string): string | undefined {
  const value = payload[key];
  return typeof value === 'string' && value.trim() ? value : undefined;
}

/**
 * Renders a `portfolio_conversation_due` workflow payload (issue #1297): the
 * daily cadence sweep noticed that a child's development talk is due. This is a
 * reminder, not an approval gate.
 *
 * The payload carries exactly four fields (models.PortfolioConversationDuePayload):
 * child_id, child_name, last_held_date (YYYY-MM-DD, optional) and cadence.
 */
export function PortfolioConversationDuePayload({
  payload,
  dueAt,
  i18n,
}: PortfolioConversationDuePayloadProps) {
  const childId = readString(payload, 'child_id');
  const childName = readString(payload, 'child_name');
  const lastHeldDate = readString(payload, 'last_held_date');
  const cadence = readString(payload, 'cadence');
  const canOpen = useCanOpen();

  // formatDate (not formatTimestamp) because last_held_date is a DATE: the talk
  // is recorded on a day, not at a time.
  const lastHeldLabel = lastHeldDate ? formatDate(lastHeldDate) : undefined;
  const dueLabel = dueAt ? formatTimestamp(dueAt) : undefined;

  return (
    <div className="space-y-4">
      {childName && (
        <FieldRow label={i18n._({ id: 'workflow.payload.childName', message: 'Child' })}>
          <p className="text-sm">{childName}</p>
        </FieldRow>
      )}

      {/* Rendered even without a value: "never held" is the fact that makes the
          reminder actionable, so the row must not silently disappear. */}
      <FieldRow
        label={i18n._({ id: 'workflow.payload.lastHeldDate', message: 'Last talk held' })}
      >
        <p className="text-sm">
          {lastHeldLabel ??
            i18n._({ id: 'workflow.payload.noTalkHeld', message: 'No talk recorded yet' })}
        </p>
      </FieldRow>

      {cadence && (
        <FieldRow label={i18n._({ id: 'workflow.payload.cadence', message: 'Cadence' })}>
          {/* Text-bearing badge, so the cadence is never colour-only. */}
          <Badge variant="outline">{cadence}</Badge>
        </FieldRow>
      )}

      {dueLabel && (
        <FieldRow label={i18n._({ id: 'workflow.column.due', message: 'Due' })}>
          <p className="text-sm">{dueLabel}</p>
        </FieldRow>
      )}

      {/* Without the children module the link would bounce, so it is not offered. */}
      {childId && canOpen(`/children/${childId}/edit`) && (
        <Link
          to={`/children/${childId}/edit?tab=portfolio`}
          className="text-sm text-primary underline underline-offset-4"
        >
          {i18n._({
            id: 'workflow.payload.openPortfolioTab',
            message: 'Open portfolio and talk preparation',
          })}
        </Link>
      )}
    </div>
  );
}
